import React from 'react';
import { ButtonGroup } from 'reactstrap';
import ButtonAction from './ButtonAction';
import ButtonDelete from './ButtonDelete';
import EditableField from './EditableField';
import { matchesSimpleString, validateShortDescription } from '../helpers/Validations';
import { formattedDate, bytesToSize } from '../helpers/GenericHelpers';
import { FILE_TYPES } from '../constants/Fixtures';
import * as warnings from '../constants/ValidationWarnings';

export default class File extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = { editing: false };
  }

  getColumns() {
    return this.props.columns || [];
  }

  clickFileNameHandle = () => {
    this.setState({ editing: true });
  }

  updateFileName = (id, attribute, newName) => {
    if (this.props.name !== newName) {
      this.props.update(this.props.id, 'name', newName);
    }
    this.setState({ editing: false });
  }

  clearEdit = () => {
    this.setState({ editing: false });
  }

  validateFileName(name) {
    return /^[a-zA-Z_\-\.0-9]+$/.test(name);
  }

  statsButton = () => (<ButtonAction
    icon="fa-external-link"
    size="sm"
    color="secondary"
    disabled={!this.props.has_stats}
    title={`File statistics for ${this.props.name}`}
    action={() => this.props.getStatistics(this.props.id, this.props.name, this.props.statistics)}
  />)

  checksButtonGroup(){
    let warnings = Object.entries(this.props.file_check_counts || {}).map((info) => {
      const level = info[0];
      const count = info[1];
      if (count > 0){
        return this.checkButtonNew(this.checksColorLevel(level), this.stringifyErrorCount(level, count));
      }
    });

    if (warnings.every(w => w === undefined)){
      warnings = this.checkButtonNew(this.checksColorLevel("success"), "No File Alerts");
    }

    return(
    <ButtonGroup size="sm">
      {warnings}
    </ButtonGroup>)
  }

  stringifyErrorCount(level, count){
    const singular = count == 1;
    switch (level) {
      case 'error':
        return singular ? count + " critical error" : count + " critical errors";
      case 'warning':
        return singular ? count + " warning" : count + " warnings";
      case 'info':
        return singular ? count + " notice" : count + " notices";
      default:
        return "No File Alerts";
    }
  }

  checkButtonNew = (color,count) => (<ButtonAction
    className="file-checks"
    size="sm"
    color={color}
    title={`File checks for ${this.props.name}`}
    action={() => this.props.getChecks(this.props.id, this.props.name, this.props.checks)}
  >
    {count}
  </ButtonAction>)

  checksButton = () => (<ButtonAction
    className="file-checks"
    size="sm"
    color={this.checksColor()}
    title={`File checks for ${this.props.name}`}
    action={() => this.props.getChecks(this.props.id, this.props.name, this.props.checks)}
  />)

  coverButton = () => (<ButtonAction
    size="sm"
    color={this.isCover() ? 'success' : 'warning'}
    className="pull-md-right"
    title="Set as cover"
    icon="fa-book"
    action={() => this.props.projectUpdate('cover', this.props.id)}
  >
    <span className="hidden-md-down">
      {this.isCover() ? 'Cover' : 'Set cover'}
    </span>
  </ButtonAction>)

  isCover() {
    return parseInt(this.props.cover) === parseInt(this.props.id);
  }

  versionTag() {
    return (<span className="ebook-label">
      {FILE_TYPES[this.props.type]}
    </span>);
  }

  originTag() {
    return (<span className="other-label">
      {this.props.origin}
    </span>);
  }

  shortDescription() {
    return (<EditableField
      id={this.props.id}
      itemRef="short_desc"
      title="Enter a short description"
      text={this.props.short_desc}
      update={this.props.update}
      textarea
      validate={validateShortDescription}
      warning={'Short descriptions cannot contain HTML control characters and must be 150 characters or fewer.'}
    />);
  }

  getColumnData = (colName) => {
    switch (colName) {
      case 'Modified':
        const date = this.props.updated_at;
        if (!date) { return; }
        return formattedDate(date);
      case 'Stats':
        return this.statsButton();
      case 'Checks':
        return this.checksButtonGroup();
      case 'Size':
        return bytesToSize(this.props.size);
      case 'Version':
        return this.versionTag();
      case 'Thumbnail':
        return <img src={`/get/thumb/${this.props.id}`} />;
      case 'Cover':
        return this.coverButton();
      case 'Origin':
        return this.originTag();
      case 'Short Description':
        return this.shortDescription();
      case 'Dimensions':
        const dims = this.props[colName.toLowerCase()];
        return !!dims && dims.length === 2 && dims.indexOf(null) === -1 ? `${dims[0]} x ${dims[1]}` : '';
      default:
        return this.props[colName.toLowerCase()];
    }
  }

  checksColor = () => {
    const level = this.props.error_level;
    switch (level) {
      case 'error':
        return 'danger';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'success';
    }
  }
  checksColorLevel = (level) => {
    switch (level) {
      case 'error':
        return 'danger';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'success';
    }
  }

  render() {
    return (<tr>
      <td className="file-name">
        <EditableField
          itemRef="file_name"
          title="Enter a file name"
          text={this.props.name}
          update={this.updateFileName}
          validate={matchesSimpleString}
          warning={warnings.SIMPLE_STRING}
        />
      </td>
      {this.getColumns().map(col =>
        (<td key={col}>
          {this.getColumnData(col)}
        </td>),
      )}
      <td className="file-download">
        <ButtonGroup size="sm">
          <a
            className="btn btn-secondary"
            title={`Download ${this.props.name}`}
            data-turbolinks={false}
            href={`/get/assets/${this.props.id}`}
          >
            <i className="fa fa-download" />
          </a>
          <ButtonDelete
            key={`${this.props.name}-del`}
            title={`Delete ${this.props.name}`}
            action={this.props.destroy}
          />
        </ButtonGroup>
      </td>
    </tr>);
  }
}
